import { BaseApi } from "../base.js";
import { API } from "../constants.js";
import { loadSecure, loadProfile } from "@/api/coinService";
import router from "@/router";
import { CAPTCHA_CHALLENGES } from "@/captcha/constants";

export class SettingsApi extends BaseApi {
  // получение отдельных кусков

  settingsGetSessions() {
    return this.request(
      () => this.r.get(`${API.ENDPOINT}/auth/sessions?skip=0&take=100`),
      (d) => this.user.setSessions(d),
    );
  }

  settingsGetNotifications() {
    return this.request(
      () => this.r.get(`${API.EMCD_API}/settings/get-notifications`),
      (d) => this.user.setNotifications(d),
    );
  }

  async getSettings() {
    // 2FA/secure — через auth-service (state.auth.secure), читается useAuth().
    const all = await Promise.all([
      loadSecure().catch(() => {}),
      this.settingsGetSessions(),
      this.settingsGetNotifications(),
      // Профиль через auth-service (state.auth.profile) вместо user.dashboard.
      loadProfile().catch(() => {}),
    ]);

    return all;
  }

  enableGoogle2fa(p) {
    return this.r.post(`${API.ENDPOINT}/users/enable-google-2fa`, p);
  }

  disableGoogle2fa() {
    this.r.set2Fa(this.disableGoogle2fa.bind(this));
    return this.request(
      () => this.r.put(`${API.ENDPOINT}/users/disable-google-2fa`),
      () => router.push({ name: "ProfileSecurity" }),
    );
  }

  // returnTo — путь или имя роута, куда вернуться после экрана подтверждения
  enableMobile2fa(returnTo) {
    return this.request(
      () => this.r.put(`${API.ENDPOINT}/users/enable-mobile-2fa`),
      () =>
        router.push({
          name: "ProfileSecurityEnable2FaPhone",
          query: { returnTo },
        }),
    );
  }

  disableMobile2fa() {
    this.r.set2Fa(this.disableMobile2fa.bind(this));
    return this.request(
      () => this.r.get(`${API.ENDPOINT}/users/disable-mobile-2fa`),
      () => router.push({ name: "ProfileSecurity" }),
    );
  }

  async verifyPhone(newPhone) {
    this.user.setVerifyPhone(newPhone.phone);
    this.r.set2Fa(this.verifyPhone.bind(this), newPhone);
    const preparedPhone = { phone: newPhone.phone.split(" ").join("") };
    // Кастомный заголовок, по которому ручка определяет что это TMA и отключает запрос капчи в этой ручке.
    const headers = { "tma-wallet": "1" };
    const url = `${API.ENDPOINT}/v2/users/change-phone`;

    return this.request(
      async () => {
        let res = await this.r.put(url, preparedPhone, headers);

        if (CAPTCHA_CHALLENGES.includes(res?.required?.[0])) {
          const captchaToken = this.user.getCaptchaToken();
          if (captchaToken) {
            const headers = {
              "secret-key": res?.secret_key,
              "captcha-token": captchaToken,
              "tma-wallet": "1",
            };
            res = await this.r.put(url, preparedPhone, headers);
          }
        }
        return res;
      },
      () => {
        this.user.setVerifyPhone(newPhone.phone);
        if (newPhone.enable2fa) {
          this.enableMobile2fa(newPhone.returnTo);
          return;
        }
        router.push({ name: "ProfileSecurity" });
      },
    );
  }

  verifyEmail(p) {
    this.r.set2Fa(this.verifyEmail.bind(this), p);
    return this.request(
      () => this.r.post(`${API.ENDPOINT}/v2/users/change-email`, p),
      () => {
        this.user.setVerifyEmail(p.email);
        router.push({
          name: "ProfileSecurityConfirmEmail",
          query: { email: p.email },
        });
      },
    );
  }

  setNotifications(d) {
    // Toggles update optimistically and the caller reports the result itself,
    // so no global loader and no generic error toast
    return this.r.put(
      `${API.EMCD_API}/settings/set-notifications`,
      d,
      undefined,
      {
        disablePreloader: true,
        silentError: true,
      },
    );
  }

  setCurrency(d) {
    return this.r.put(`${API.EMCD_API}/settings/set-currency`, d);
  }

  logoutSession(token) {
    return this.r.post(`${API.ENDPOINT}/auth/logout`, { refresh_token: token });
  }

  logoutAll() {
    return this.r.post(`${API.ENDPOINT}/auth/sessions/close-all`);
  }
}
